import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Map from '../components/Map';
import MyPOIsPanel from '../components/MyPOIsPanel';
import CreateCardPanel from '../components/CreateCardPanel';
import POIForm from '../components/POIForm';
import LocationControl from '../components/LocationControl';
import { poiService, POI, CreatePOIRequest, CreateCardRequest } from '../services/poiService';
import { useAuth } from '../contexts/AuthContext';
import { useGeolocation } from '../hooks/useGeolocation';
import { MapPin } from 'lucide-react';

const CreatorStudioPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const hasInitiallyCentered = useRef(false);

  // POI and Card states
  const [pois, setPois] = useState<POI[]>([]);
  const [selectedPOI, setSelectedPOI] = useState<POI | null>(null);
  const [selectedPOIForCard, setSelectedPOIForCard] = useState<POI | null>(null);
  const [isLoadingPOIs, setIsLoadingPOIs] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState<{ current: number; total: number } | null>(null);

  // Map states
  const [mapCenter, setMapCenter] = useState<[number, number]>([44.4268, 26.1025]); // Default to Bucharest
  const [centerOnUser, setCenterOnUser] = useState(false);

  // UI states
  const [showPOIForm, setShowPOIForm] = useState(false);
  const [showCardPanel, setShowCardPanel] = useState(false);
  const [newPOILocation, setNewPOILocation] = useState<[number, number] | null>(null);
  const [isCreatingPOI, setIsCreatingPOI] = useState(false);

  // Geolocation
  const {
    latitude,
    longitude,
    error: locationError,
    loading: locationLoading,
    supported: locationSupported,
    requestLocation,
    stopTracking,
    isTracking,
  } = useGeolocation({
    enableHighAccuracy: true,
    timeout: 15000,
    maximumAge: 300000,
    watch: false,
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Update map center when user location is first obtained
  useEffect(() => {
    if (latitude && longitude && !hasInitiallyCentered.current) {
      setMapCenter([latitude, longitude]);
      setCenterOnUser(true);
      hasInitiallyCentered.current = true;
      setTimeout(() => setCenterOnUser(false), 500);
    }
  }, [latitude, longitude]);

  // Load POIs
  useEffect(() => {
    loadPOIs();
  }, []);

  const loadPOIs = async (centerLat?: number, centerLng?: number) => {
    try {
      setIsLoadingPOIs(true);
      setLoadingProgress(null);
      
      // Priority: explicit coordinates > user location > map center
      const lat = centerLat ?? latitude ?? mapCenter[0];
      const lng = centerLng ?? longitude ?? mapCenter[1];
      const radiusMeters = 50000; // 50km radius to capture nearby POIs
      
      console.log(`Fetching POIs within ${radiusMeters/1000}km of (${lat}, ${lng})`);
      
      // Fetch all POIs with pagination for comprehensive coverage
      let allPois: POI[] = [];
      let offset = 0;
      const limit = 100; // Use maximum allowed limit
      let hasMore = true;
      let totalPOIs = 0;

      while (hasMore) {
        const queryParams = { 
          latitude: lat, 
          longitude: lng, 
          radius: radiusMeters, 
          limit, 
          offset 
        };

        const response = await poiService.fetchPOIs(queryParams);
        allPois = [...allPois, ...response.data];
        hasMore = response.pagination.has_more;
        totalPOIs = response.pagination.total;
        offset += limit;
        
        // Update loading progress
        setLoadingProgress({ current: allPois.length, total: totalPOIs });
        
        // Add a small delay to avoid overwhelming the server
        if (hasMore) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      console.log(`Loaded ${allPois.length} POIs total`);
      setPois(allPois);
    } catch (error) {
      console.error('Error loading POIs:', error);
    } finally {
      setIsLoadingPOIs(false);
      setLoadingProgress(null);
    }
  };

  // Handle map movement to fetch new POIs
  const handleMapMove = async (lat: number, lng: number) => {
    console.log(`Map moved to (${lat}, ${lng}), fetching new POIs...`);
    // Update map center to reflect the new position
    setMapCenter([lat, lng]);
    
    // Fetch new POIs for the new location
    await loadPOIs(lat, lng);
  };

  const handleMapClick = (lat: number, lng: number) => {
    // When clicking on map, allow POI creation
    setNewPOILocation([lat, lng]);
    setShowPOIForm(true);
  };

  const handlePOIMarkerClick = (poi: POI) => {
    // When clicking a POI marker, select it for card creation
    setSelectedPOIForCard(poi);
    setShowCardPanel(true);
  };

  const handleCreatePOI = () => {
    // Trigger POI creation - user needs to click on map
    setShowPOIForm(false);
    setNewPOILocation(null);
    // Could show a toast notification here
    console.log('Click on the map to place your new POI');
  };

  const handlePOISubmit = async (poiData: CreatePOIRequest) => {
    try {
      setIsCreatingPOI(true);
      const response = await poiService.createPOI(poiData);
      
      // Reload POIs
      await loadPOIs();
      
      // Close form
      setShowPOIForm(false);
      setNewPOILocation(null);
      
      // Select the new POI
      setSelectedPOI(response.data);
    } catch (error) {
      console.error('Error creating POI:', error);
      throw error;
    } finally {
      setIsCreatingPOI(false);
    }
  };

  const handleCardSubmit = async (cardData: any) => {
    try {
      await poiService.createCard(cardData as CreateCardRequest);
      
      // Reload POIs to update card counts
      await loadPOIs();
      
      // Close panel
      setShowCardPanel(false);
      setSelectedPOIForCard(null);
    } catch (error) {
      console.error('Error creating card:', error);
      throw error;
    }
  };

  const handleSelectPOI = (poi: POI) => {
    setSelectedPOI(poi);
    // Center map on POI
    setMapCenter([parseFloat(poi.latitude), parseFloat(poi.longitude)]);
  };

  const handleCenterOnUser = () => {
    if (latitude && longitude) {
      setMapCenter([latitude, longitude]);
      setCenterOnUser(true);
      setTimeout(() => setCenterOnUser(false), 500);
    } else {
      requestLocation();
    }
  };

  return (
    <div className="relative w-full h-full">
      {/* Map Background */}
      <div className="absolute inset-0">
        <Map
          pois={pois}
          onMapClick={handleMapClick}
          onPOIClick={handlePOIMarkerClick}
          onMapMove={handleMapMove}
          center={mapCenter}
          centerOnUser={centerOnUser}
          userLocation={latitude && longitude ? { latitude, longitude } : null}
          zoom={13}
        />
      </div>

      {/* Location Control */}
      <div className="absolute left-6 bottom-6 z-20">
        <LocationControl
          onCenterOnUser={handleCenterOnUser}
          isTracking={isTracking}
          hasLocation={!!(latitude && longitude)}
          error={locationError}
          loading={locationLoading}
          supported={locationSupported}
          onRequestLocation={requestLocation}
          onStopTracking={stopTracking}
        />
      </div>

      {/* Floating Right Panels */}
      <div className="absolute right-6 top-6 z-20 space-y-4">
        {/* My POIs Panel */}
        <MyPOIsPanel
          onCreatePOI={handleCreatePOI}
          onSelectPOI={handleSelectPOI}
          userLocation={latitude && longitude ? { latitude, longitude } : null}
        />

        {/* Create Card Button (when POI selected) */}
        {selectedPOI && !showCardPanel && (
          <button
            onClick={() => {
              setSelectedPOIForCard(selectedPOI);
              setShowCardPanel(true);
            }}
            className="w-80 bg-creator-accent hover:bg-opacity-90 text-white font-medium py-3 rounded-2xl shadow-lg transition-all flex items-center justify-center space-x-2"
          >
            <MapPin className="w-5 h-5" />
            <span>Create Card for {selectedPOI.name}</span>
          </button>
        )}
      </div>

      {/* POI Creation Form Modal */}
      {showPOIForm && newPOILocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <POIForm
              location={{ lat: newPOILocation[0], lng: newPOILocation[1] }}
              onSubmit={handlePOISubmit}
              onCancel={() => {
                setShowPOIForm(false);
                setNewPOILocation(null);
              }}
              isLoading={isCreatingPOI}
            />
          </div>
        </div>
      )}

      {/* Card Creation Panel */}
      {showCardPanel && (
        <CreateCardPanel
          selectedPOI={selectedPOIForCard}
          onClose={() => {
            setShowCardPanel(false);
            setSelectedPOIForCard(null);
          }}
          onSubmit={handleCardSubmit}
        />
      )}

      {/* Loading Overlay */}
      {isLoadingPOIs && (
        <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center z-30">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-xl">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-creator-accent mx-auto mb-3"></div>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {loadingProgress 
                ? `Loading POIs... ${loadingProgress.current}/${loadingProgress.total}`
                : 'Loading POIs...'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorStudioPage;
